import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, TextField, Button,
  Alert, CircularProgress, Chip, Divider, Tooltip, IconButton,
  InputAdornment, Switch, FormControlLabel, FormGroup,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Business as InstitutionIcon,
  School as AcademicIcon,
  Sync as SyncIcon,
  Security as SecurityIcon,
  Tune as SystemIcon,
  Save as SaveIcon,
  Refresh as ResetIcon,
  Info as InfoIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

// ── Category metadata ─────────────────────────────────────────────────────────
const CATEGORIES = {
  institution: { label: 'Institution',        icon: <InstitutionIcon />, color: '#2980b9' },
  academic:    { label: 'Académique',          icon: <AcademicIcon />,    color: '#27ae60' },
  sync:        { label: 'Synchronisation',     icon: <SyncIcon />,        color: '#e67e22' },
  security:    { label: 'Sécurité',            icon: <SecurityIcon />,    color: '#e74c3c' },
  menu:        { label: 'Menu Étudiant',        icon: <MenuIcon />,        color: '#0891b2' },
  system:      { label: 'Système',             icon: <SystemIcon />,      color: '#8e44ad' },
};
const CAT_ORDER = ['institution', 'academic', 'sync', 'security', 'menu', 'system'];

// Years available for selection (covers realistic range)
const YEAR_OPTIONS = Array.from({ length: 8 }, (_, i) => String(2020 + i));


// ── Single setting field ──────────────────────────────────────────────────────
const SettingField = ({ setting, value, onChange, disabled = false }) => {
  const color = CATEGORIES[setting.category]?.color || '#607d8b';

  const label = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
      <Typography variant="body2" fontWeight={700} sx={{ color: disabled ? '#94a3b8' : '#1e293b' }}>
        {setting.label}
      </Typography>
      <Chip
        label={setting.type}
        size="small"
        sx={{ height: 16, fontSize: '0.6rem', bgcolor: color + '18', color, borderRadius: 1, fontWeight: 700 }}
      />
      {setting.description && (
        <Tooltip title={setting.description} placement="right">
          <InfoIcon sx={{ fontSize: 14, color: '#94a3b8', cursor: 'help' }} />
        </Tooltip>
      )}
      {disabled && (
        <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
          (page désactivée)
        </Typography>
      )}
    </Box>
  );

  // ── Boolean toggle ──
  if (setting.type === 'boolean') {
    const checked = value === 'true';
    return (
      <Box sx={{ mb: 3, opacity: disabled ? 0.55 : 1 }}>
        {label}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 2, border: `1px solid ${color}30`, bgcolor: checked && !disabled ? color + '08' : '#f8fafc' }}>
          <FormControlLabel
            control={
              <Switch
                checked={checked}
                disabled={disabled}
                onChange={e => onChange(setting.key, e.target.checked ? 'true' : 'false')}
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: color } }}
              />
            }
            label={
              <Typography variant="body2" fontWeight={600} sx={{ color: checked && !disabled ? color : 'text.secondary' }}>
                {checked ? 'Activé' : 'Désactivé'}
              </Typography>
            }
          />
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#94a3b8' }}>
            {setting.key}
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── Notes & Résultats visibility ──
  if (setting.key === 'menu.grades.diplomes') {
    const OPTIONS = [
      { value: '',              label: 'Tous les étudiants',          desc: 'Tous voient les notes' },
      { value: 'EXCEPT_PEDAGO', label: 'Hors Structure Pédagogique',  desc: 'Les filières de la structure pédagogique ne voient pas les notes' },
    ];
    return (
      <Box sx={{ mb: 3 }}>
        {label}
        <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, borderRadius: 2, border: `1px solid ${color}30`, flexWrap: 'wrap' }}>
          {OPTIONS.map(opt => {
            const selected = value === opt.value;
            return (
              <Chip
                key={opt.value || 'all'}
                label={opt.label}
                clickable
                title={opt.desc}
                onClick={() => onChange(setting.key, opt.value)}
                sx={{
                  fontWeight: 700, fontSize: '0.88rem', px: 1,
                  bgcolor: selected ? color : '#f1f5f9',
                  color: selected ? 'white' : '#64748b',
                  '&:hover': { opacity: 0.9 },
                }}
              />
            );
          })}
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.4, display: 'block' }}>
          "Hors Structure Pédagogique" — les filières définies dans la Structure Pédagogique sont exclues ; tout le reste peut voir les notes.
        </Typography>
      </Box>
    );
  }

  // ── Semester multi-select (for pedago_visible_semesters) ──
  if (setting.key === 'academic.pedago_visible_semesters') {
    const SEM_OPTIONS = ['S1', 'S2', 'S3', 'S4', 'S5', 'S6'];
    const selected = new Set((value || '').split(',').map(s => s.trim()).filter(Boolean));
    const toggle = (sem) => {
      const next = new Set(selected);
      next.has(sem) ? next.delete(sem) : next.add(sem);
      onChange(setting.key, Array.from(next).sort().join(','));
    };
    return (
      <Box sx={{ mb: 3 }}>
        {label}
        <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, borderRadius: 2, border: `1px solid ${color}30`, flexWrap: 'wrap' }}>
          {SEM_OPTIONS.map(sem => {
            const on = selected.has(sem);
            return (
              <Chip
                key={sem}
                label={sem}
                clickable
                onClick={() => toggle(sem)}
                sx={{
                  fontWeight: 700, fontSize: '0.9rem', px: 1,
                  bgcolor: on ? color : '#f1f5f9',
                  color: on ? 'white' : '#64748b',
                  '&:hover': { bgcolor: on ? color : '#e2e8f0', opacity: 0.9 }
                }}
              />
            );
          })}
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.4, display: 'block' }}>
          Licence uniquement (JL%) — les semestres désactivés sont masqués dans la Situation Pédagogique
        </Typography>
      </Box>
    );
  }

  // ── Year multi-select (for visible_years) ──
  if (setting.key === 'academic.visible_years') {
    const selected = new Set((value || '').split(',').map(y => y.trim()).filter(Boolean));
    const toggle = (yr) => {
      const next = new Set(selected);
      next.has(yr) ? next.delete(yr) : next.add(yr);
      onChange(setting.key, Array.from(next).sort().join(','));
    };
    return (
      <Box sx={{ mb: 3 }}>
        {label}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1.5, borderRadius: 2, border: `1px solid ${color}30` }}>
          {YEAR_OPTIONS.map(yr => {
            const on = selected.has(yr);
            return (
              <Chip
                key={yr}
                label={`${yr}/${String(parseInt(yr) + 1).slice(-2)}`}
                clickable
                onClick={() => toggle(yr)}
                sx={{
                  fontWeight: 700,
                  bgcolor: on ? color : '#f1f5f9',
                  color: on ? 'white' : '#64748b',
                  '&:hover': { bgcolor: on ? color : '#e2e8f0', opacity: 0.9 }
                }}
              />
            );
          })}
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.4, display: 'block' }}>
          Cliquez sur une année pour l'activer / désactiver pour les étudiants
        </Typography>
      </Box>
    );
  }

  // ── Default: text field ──
  return (
    <Box sx={{ mb: 3 }}>
      {label}
      <TextField
        fullWidth
        size="small"
        value={value}
        onChange={e => onChange(setting.key, e.target.value)}
        multiline={value.length > 60}
        rows={value.length > 60 ? 2 : 1}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#94a3b8', fontSize: '0.65rem' }}>
                {setting.key}
              </Typography>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            '&.Mui-focused fieldset': { borderColor: color },
          },
        }}
      />
      {setting.type === 'list' && (
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.4, display: 'block' }}>
          Valeurs séparées par des virgules
        </Typography>
      )}
    </Box>
  );
};

// ── Category panel ────────────────────────────────────────────────────────────
const CategoryPanel = ({ category, settings, values, onChange, onSave, saving, saved }) => {
  const meta  = CATEGORIES[category] || { label: category, icon: <SettingsIcon />, color: '#607d8b' };
  const color = meta.color;

  // Build a set of all setting keys in this category for parent-lookup
  const settingKeySet = new Set(settings.map(s => s.key));

  // A setting is a sub-setting if removing its last ".segment" yields an existing key
  const getParentKey = (key) => {
    const idx = key.lastIndexOf('.');
    if (idx === -1) return null;
    const candidate = key.substring(0, idx);
    return settingKeySet.has(candidate) ? candidate : null;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
            {meta.icon}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700}>{meta.label}</Typography>
            <Typography variant="caption" color="text.secondary">{settings.length} paramètre(s)</Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={onSave}
          disabled={saving}
          sx={{ borderRadius: 2, bgcolor: color, boxShadow: 'none', '&:hover': { bgcolor: color, filter: 'brightness(0.9)', boxShadow: 'none' } }}
        >
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </Box>

      {saved && (
        <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => {}}>
          Paramètres enregistrés avec succès.
        </Alert>
      )}

      <Paper elevation={0} sx={{ borderRadius: 3, border: `1.5px solid ${color}30`, p: 3 }}>
        {settings.map((s, i) => {
          const parentKey     = getParentKey(s.key);
          const isSubSetting  = !!parentKey;
          // Sub-setting is disabled when its parent boolean is explicitly 'false'
          const parentDisabled = isSubSetting && (values[parentKey] ?? 'true') === 'false';

          return (
            <React.Fragment key={s.key}>
              {isSubSetting ? (
                <Box sx={{
                  pl: 2.5,
                  ml: 1,
                  borderLeft: `3px solid ${color}40`,
                  borderRadius: '0 4px 4px 0',
                }}>
                  <SettingField
                    setting={s}
                    value={values[s.key] ?? s.value}
                    onChange={onChange}
                    disabled={parentDisabled}
                  />
                </Box>
              ) : (
                <SettingField setting={s} value={values[s.key] ?? s.value} onChange={onChange} />
              )}
              {i < settings.length - 1 && <Divider sx={{ mb: 3 }} />}
            </React.Fragment>
          );
        })}
      </Paper>
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const AdminSettings = () => {
  const [grouped, setGrouped]   = useState({});
  const [values, setValues]     = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving]     = useState(false);
  const [savedCat, setSavedCat] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getSettings();
      setGrouped(data.grouped || data.settings || {});
      const flat = {};
      (data.flat || []).forEach(s => { flat[s.key] = s.value; });
      setValues({ ...flat });
      setOriginal({ ...flat });
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = (key, val) => setValues(prev => ({ ...prev, [key]: val }));

  const handleSave = async (category) => {
    const catSettings = grouped[category] || [];
    const updates = catSettings
      .filter(s => values[s.key] !== original[s.key])
      .map(s => ({ key: s.key, value: values[s.key] }));

    // Always save all in category (even unchanged) so admin intent is explicit
    const allUpdates = catSettings.map(s => ({ key: s.key, value: values[s.key] ?? s.value }));

    setSaving(true);
    setSavedCat('');
    try {
      await adminAPI.updateSettings(allUpdates);
      setOriginal(prev => {
        const next = { ...prev };
        allUpdates.forEach(u => { next[u.key] = u.value; });
        return next;
      });
      setSavedCat(category);
      setTimeout(() => setSavedCat(''), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setValues({ ...original });
  };

  const orderedCats = CAT_ORDER.filter(c => grouped[c]);

  if (loading) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 6 }}>
      <CircularProgress />
    </Box>
  );

  // Count changed values
  const changedCount = Object.keys(values).filter(k => values[k] !== original[k]).length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900 }}>

      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a2a3a 0%, #2c3e50 60%, #3d566e 100%)',
        borderRadius: 4, p: { xs: 2.5, md: 3 }, mb: 3, color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SettingsIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700}>Paramètres de l'Application</Typography>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              {Object.values(grouped).flat().length} paramètre(s) configurables
            </Typography>
          </Box>
        </Box>
        {changedCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${changedCount} modification(s) non enregistrée(s)`}
              size="small"
              sx={{ bgcolor: '#f39c12', color: 'white', fontWeight: 700 }}
            />
            <Tooltip title="Annuler les modifications">
              <IconButton size="small" onClick={handleReset} sx={{ color: 'white' }}>
                <ResetIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef', mb: 3, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: '1px solid #e8ecef',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 52 },
          }}
        >
          {orderedCats.map((cat, i) => {
            const meta  = CATEGORIES[cat] || { label: cat, icon: <SettingsIcon /> };
            const count = (grouped[cat] || []).filter(s => values[s.key] !== original[s.key]).length;
            return (
              <Tab
                key={cat}
                value={i}
                icon={meta.icon}
                iconPosition="start"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {meta.label}
                    {count > 0 && (
                      <Chip label={count} size="small" sx={{ height: 18, minWidth: 18, fontSize: '0.65rem', bgcolor: meta.color, color: 'white', borderRadius: 1 }} />
                    )}
                  </Box>
                }
                sx={{ color: meta.color }}
              />
            );
          })}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {orderedCats.map((cat, i) => (
            activeTab === i && (
              <CategoryPanel
                key={cat}
                category={cat}
                settings={grouped[cat] || []}
                values={values}
                onChange={handleChange}
                onSave={() => handleSave(cat)}
                saving={saving}
                saved={savedCat === cat}
              />
            )
          ))}
        </Box>
      </Paper>

      {/* Legend */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef', p: 2 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={1}>
          Types de valeurs
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {[
            { type: 'string', desc: 'Texte libre' },
            { type: 'number', desc: 'Valeur numérique' },
            { type: 'list',   desc: 'Liste séparée par des virgules' },
          ].map(({ type, desc }) => (
            <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip label={type} size="small" sx={{ height: 18, fontSize: '0.65rem', borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary">{desc}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.disabled" display="block" mt={1.5}>
          ⚠️ Certains paramètres (ex: années de sync, codes Oracle) nécessitent un redémarrage du serveur ou une nouvelle synchronisation pour prendre effet.
        </Typography>
      </Paper>
    </Box>
  );
};

export default AdminSettings;
